import { useEffect, useState } from 'react';
import { cta } from '../../content';
import { Arrow, Button, SmartLink, Swipe, cx } from '../../ui';
import { MakerPlate, type PlateSpec } from '../../ui/brandcode';

// Moved to shared ui; re-exported so existing imports keep working.
export { MakerPlate, type PlateSpec };
import type { SectorPage } from './data';

// Sector heroes in the house style (Variant C): a real photo, and the maker's own plate or label with
// their code made part of it. The maker leads; BrandedMark is not on the product at all.
// Three compositions so the sector pages never read as one template: full-bleed, card right, card left.
// Every maker, model and serial here is fictional.

export type SectorHeroSpec = {
  layout: 'bleed' | 'right' | 'left';
  img: string;
  alt: string;
  pos?: string; // object-position
  fallback?: string; // shown while a generated photo is missing; with none, the photo is hidden
  plate: PlateSpec;
};

export const SECTOR_HEROES: Record<string, SectorHeroSpec> = {
  'agricultural-machinery': {
    layout: 'bleed',
    img: '/images/v2/gen/agri-field.jpg',
    alt: 'A round baler working a field of cut hay at golden hour, towed by a tractor',
    pos: '62% 50%',
    plate: {
      finish: 'steel',
      maker: 'Harrowfield',
      kind: 'Round baler',
      mark: 'H',
      accent: '#2E4A3E',
      rows: [
        ['Model', 'RB160'],
        ['Serial no.', 'HF-RB160-24-0417'],
        ['Year', '2024'],
        ['Mass', '3,850 kg'],
      ],
      serialKey: 'Serial no.',
      meet: 'Meet your RB160',
    },
  },
  'lifting-access-equipment': {
    layout: 'bleed',
    img: '/images/v2/gen/lifting-inspection.jpg',
    alt: 'An engineer in a hi-vis vest inspecting a scissor lift in a bright warehouse',
    pos: '70% 40%',
    plate: {
      finish: 'fleet',
      maker: 'Talbrook',
      kind: 'Scissor lift, MEWP',
      mark: 'T',
      accent: '#1B1B1B',
      rows: [
        ['Model', 'TX12'],
        ['Serial no.', 'TB-TX12-2023-1186'],
        ['Safe working load', '450 kg, 2 persons'],
        ['Year', '2023'],
      ],
      serialKey: 'Serial no.',
      meet: 'Meet your TX12',
    },
  },
  'heating-heat-pumps': {
    layout: 'right',
    img: '/images/v2/gen/heatpump-commission-b.jpg',
    alt: 'An installer kneeling beside an outdoor heat pump unit in a garden',
    pos: '60% 50%',
    plate: {
      finish: 'label',
      maker: 'Halvard',
      kind: 'Air to water heat pump',
      mark: 'K',
      accent: '#2E4A3E',
      rows: [
        ['Model', 'AW-9'],
        ['Serial no.', 'KAW9-26-04182'],
        ['Refrigerant', 'R290'],
        ['Year', '2026'],
      ],
      serialKey: 'Serial no.',
      meet: 'Start here',
    },
  },
  'commercial-kitchen': {
    layout: 'right',
    img: '/images/v2/gen/kitchen-service.jpg',
    alt: 'A service engineer opening the side panel of a stainless combination oven in a commercial kitchen',
    pos: '58% 50%',
    plate: {
      finish: 'steel',
      maker: 'Fornell',
      kind: 'Combination oven',
      mark: 'F',
      accent: '#2D2D2D',
      monoInk: '#D4A045',
      rows: [
        ['Model', 'CX10, 10 grid'],
        ['Serial no.', 'FN-CX10-24-55021'],
        ['Supply', '400 V 3N~, 18.6 kW'],
        ['Year', '2024'],
      ],
      serialKey: 'Serial no.',
      meet: 'Meet your CX10',
    },
  },
  'industrial-precision': {
    layout: 'left',
    img: '/images/v2/gen/precision-dispatch.jpg',
    alt: 'A dispatcher laying the delivery note pouch on a crate of machined flanges, ready to ship',
    pos: '42% 40%',
    plate: {
      finish: 'slip',
      maker: 'Lotwerk',
      kind: 'Delivery note',
      mark: 'L',
      accent: '#1F3B57',
      rows: [
        ['Delivery', 'D-24118'],
        ['Lot', 'L-24-0918'],
        ['Certificate', 'EN 10204 3.1'],
        ['Passport', 'Ready'],
      ],
      serialKey: 'Lot',
      meet: 'This delivery, line by line',
    },
  },
  'premium-durables': {
    layout: 'left',
    img: '/images/v2/gen/hifi-owner.jpg',
    alt: 'An owner turning the volume knob of an amplifier at home',
    pos: '40% 50%',
    plate: {
      finish: 'anodised',
      maker: 'Aldwyn',
      kind: 'Integrated amplifier',
      mark: 'A',
      accent: '#D4A045',
      qrInk: '#D9DAE0',
      monoInk: '#1D1E22',
      rows: [
        ['Model', 'A-40'],
        ['Serial no.', 'AA40-2019-07733'],
        ['Mains', '230 V~ 50 Hz'],
        ['Made', '2019'],
      ],
      serialKey: 'Serial no.',
      meet: 'Meet your A-40',
    },
  },
  'medical-laboratory-instruments': {
    layout: 'left',
    img: '/images/v2/gen/instr-ppm.jpg',
    alt: 'A service engineer checking a benchtop centrifuge in a hospital laboratory',
    pos: '55% 50%',
    fallback: '/images/v2/gen/unit-centrifuge.jpg',
    plate: {
      finish: 'label',
      maker: 'Brennock',
      kind: 'Refrigerated centrifuge',
      mark: 'B',
      accent: '#0F6E8C',
      rows: [
        ['Model', 'CR-4'],
        ['Serial no.', 'BRK-CR4-25-01376'],
        ['Supply', '230 V~ 50 Hz, 1.2 kW'],
        ['Year', '2025'],
      ],
      serialKey: 'Serial no.',
      meet: 'Meet your CR-4',
    },
  },
  'sports-fitness-equipment': {
    layout: 'bleed',
    img: '/images/v2/gen/fitness-service.jpg',
    alt: 'A technician kneeling to service a treadmill in a gym in the morning sun',
    pos: '72% 50%',
    plate: {
      finish: 'anodised',
      maker: 'Strideworth',
      kind: 'Commercial treadmill',
      mark: 'S',
      accent: '#D4462A',
      monoInk: '#FFFFFF',
      rows: [
        ['Model', 'T9'],
        ['Serial no.', 'SW-T9-24-20871'],
        ['Max user', '180 kg'],
        ['Year', '2024'],
      ],
      serialKey: 'Serial no.',
      meet: 'Meet your T9',
    },
  },
};

export function SectorHero({ name, sub, slug, page, spec }: { name: string; sub: string; slug: string; page: SectorPage; spec: SectorHeroSpec }) {
  const bleed = spec.layout === 'bleed';
  const label = `${spec.plate.finish === 'slip' ? 'Delivery label' : 'Rating plate'} of a fictional ${spec.plate.kind.toLowerCase()} by ${spec.plate.maker}, with the maker's own code printed into it`;
  const copy = (
    <div className="indh-copy">
      <div className="indh-kick">
        <span className="bar" aria-hidden="true" />
        <nav aria-label="Breadcrumb">
          <SmartLink href="/industries">Industries</SmartLink>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">{name}</span>
        </nav>
      </div>
      <h1 className="t-h1 indh-h1">
        {page.h1[0]} <Swipe draw>{page.h1[1]}</Swipe> {page.h1[2]}
      </h1>
      <p className="t-lede indh-sub">{sub}</p>
      <div className="indh-cta">
        <Button href={`${cta.primaryHref}?src=industry-${slug}`} kind={bleed ? 'go' : 'primary'}>
          {cta.primary} <Arrow />
        </Button>
        <SmartLink href={cta.secondaryHref} className="indh-quiet">
          or {cta.secondary.toLowerCase()}
        </SmartLink>
      </div>
      <ul className="indh-proof">
        <li>Works with your ERP</li>
        <li>GDPR</li>
      </ul>
    </div>
  );
  const img = <HeroImg spec={spec} />;
  const plate = (
    <div className="indh-plate" role="img" aria-label={label}>
      <MakerPlate p={spec.plate} />
    </div>
  );

  return (
    <section className={cx('indh', `is-${spec.layout}`, bleed && 'on-dark')}>
      <style>{HERO_CSS}</style>
      {bleed ? (
        <>
          <div className="indh-bg">
            {img}
            <div className="indh-scrim" aria-hidden="true" />
          </div>
          <div className="wrap indh-grid">
            {copy}
            {plate}
          </div>
        </>
      ) : (
        <div className="wrap indh-grid">
          {copy}
          <div className="indh-vis">
            {img}
            <div className="indh-scrim" aria-hidden="true" />
            {plate}
          </div>
        </div>
      )}
    </section>
  );
}

// Generated photos can land after the page ships: try the photo, then its fallback, then show the plain card.
function HeroImg({ spec }: { spec: SectorHeroSpec }) {
  const [cur, setCur] = useState<string | null>(spec.img);
  useEffect(() => setCur(spec.img), [spec.img]);
  if (!cur) return null;
  return (
    <img
      src={cur}
      alt={spec.alt}
      width={1536}
      height={1024}
      className="indh-img"
      style={spec.pos && cur === spec.img ? { objectPosition: spec.pos } : undefined}
      onError={() => setCur(cur === spec.img && spec.fallback ? spec.fallback : null)}
    />
  );
}

export const HERO_CSS = `
.v2 .indh { position: relative; overflow: hidden; padding: clamp(28px, 4vw, 56px) 0 clamp(56px, 6vw, 88px); }
.v2 .indh-grid { display: grid; gap: 40px; }
.v2 .indh-kick { display: flex; align-items: center; gap: 12px; font-size: 14px; font-weight: 600; color: var(--bm-emerald); }
.v2 .indh-kick .bar { margin: 0; width: 32px; }
.v2 .indh-kick a { color: var(--bm-muted); font-weight: 500; }
.v2 .indh-kick a:hover { color: var(--bm-indigo); }
.v2 .indh-kick nav span[aria-hidden] { color: var(--bm-line); margin: 0 4px; }
.v2 .indh-h1 { margin-top: 20px; max-width: 16ch; font-size: clamp(38px, 4.4vw, 62px); line-height: 1.02; letter-spacing: -.045em; text-wrap: balance; }
.v2 .indh.is-right .indh-h1, .v2 .indh.is-left .indh-h1 { font-size: clamp(34px, 3.4vw, 50px); }
.v2 .indh-copy { min-width: 0; }
.v2 .indh-sub { margin-top: 22px; max-width: 50ch; }
.v2 .indh-cta { margin-top: 32px; display: flex; flex-wrap: wrap; align-items: center; gap: 12px 24px; }
.v2 .indh-quiet { font-size: 15.5px; font-weight: 500; color: var(--bm-muted); border-bottom: 1px solid var(--bm-line); padding-bottom: 1px; }
.v2 .indh-quiet:hover { color: var(--bm-indigo); border-color: var(--bm-indigo); }
.v2 .indh-proof { margin-top: 30px; display: flex; flex-wrap: wrap; gap: 6px 26px; list-style: none; font-size: 14px; color: var(--bm-muted); }
.v2 .indh-proof li { display: flex; align-items: center; gap: 9px; }
.v2 .indh-proof li::before { content: ''; width: 15px; height: 15px; flex: none; background: var(--bm-green); -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='m4.5 10.5 3.5 3.5 7.5-8' fill='none' stroke='%23000' stroke-width='2.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='m4.5 10.5 3.5 3.5 7.5-8' fill='none' stroke='%23000' stroke-width='2.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/contain no-repeat; }

/* card compositions */
.v2 .indh-vis { position: relative; isolation: isolate; overflow: hidden; border-radius: 28px; background: var(--bm-indigo); min-height: 600px; display: flex; align-items: flex-end; padding: 20px; }
.v2 .indh-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; z-index: -2; }
.v2 .indh-vis .indh-scrim { position: absolute; inset: 0; z-index: -1; background: linear-gradient(180deg, rgba(24,19,67,0) 40%, rgba(24,19,67,.72) 100%); }
.v2 .indh-plate { width: 100%; max-width: 500px; }
.v2 .indh.is-left .indh-vis { justify-content: flex-end; }
@media (min-width: 1024px) {
  .v2 .indh.is-right .indh-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1.04fr); gap: 56px; align-items: center; }
  .v2 .indh.is-left .indh-grid { grid-template-columns: minmax(0, 1.04fr) minmax(0, 1fr); gap: 64px; align-items: center; }
  .v2 .indh.is-left .indh-vis { order: -1; }
}

/* full bleed: the photo carries the page, type sits on the calm side */
.v2 .indh.is-bleed { background: var(--bm-indigo-deep); padding: 0; }
.v2 .indh-bg { position: absolute; inset: 0; z-index: 0; }
.v2 .indh-bg .indh-img { z-index: 0; }
.v2 .indh-bg .indh-scrim { position: absolute; inset: 0; background:
  linear-gradient(90deg, rgba(24,19,67,.95) 0%, rgba(24,19,67,.88) 38%, rgba(24,19,67,.62) 52%, rgba(24,19,67,.16) 70%, rgba(24,19,67,0) 82%),
  linear-gradient(180deg, rgba(24,19,67,0) 55%, rgba(24,19,67,.55) 100%); }
.v2 .indh.is-bleed .indh-grid { position: relative; z-index: 1; padding-top: clamp(56px, 7vw, 96px); padding-bottom: clamp(48px, 5vw, 72px); }
.v2 .indh.is-bleed .indh-kick { color: var(--bm-lime); }
.v2 .indh.is-bleed .indh-kick a { color: rgba(255,255,255,.7); }
.v2 .indh.is-bleed .indh-kick a:hover { color: #fff; }
.v2 .indh.is-bleed .indh-kick nav span[aria-hidden] { color: rgba(255,255,255,.35); }
.v2 .indh.is-bleed .indh-quiet { color: rgba(255,255,255,.75); border-color: rgba(255,255,255,.3); }
.v2 .indh.is-bleed .indh-quiet:hover { color: #fff; border-color: #fff; }
.v2 .indh.is-bleed .indh-proof { color: rgba(255,255,255,.72); }
@media (min-width: 1024px) {
  .v2 .indh.is-bleed .indh-grid { min-height: clamp(660px, 84vh, 780px); grid-template-columns: minmax(0, 1fr) minmax(0, .82fr); gap: 48px; align-items: end; }
  .v2 .indh.is-bleed .indh-copy { align-self: center; }
  .v2 .indh.is-bleed .indh-plate { justify-self: end; }
}
@media (max-width: 1023px) {
  .v2 .indh.is-bleed .indh-bg { position: relative; height: 300px; }
  .v2 .indh.is-bleed .indh-bg .indh-scrim { background: linear-gradient(180deg, rgba(24,19,67,0) 30%, rgba(24,19,67,.75) 62%, rgba(24,19,67,1) 82%); }
  .v2 .indh.is-bleed .indh-grid { padding-top: 0; margin-top: -56px; }
  .v2 .indh-vis { min-height: 520px; }
}
@media (max-width: 640px) {
  .v2 .indh-vis { min-height: 460px; padding: 12px; border-radius: 22px; margin: 0 -8px; }
  .v2 .indh-h1 { font-size: 37px; }
  .v2 .indh.is-bleed .indh-bg { height: 240px; }
}

`;
